import type { FC, ComponentProps, PropsWithChildren } from 'react';
import React, { isValidElement } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { ByLine, ByLineFromFragment } from '@confluence/content-header/entry-points/ByLine.ts';
import type { BylineClassification_content$key } from '@confluence/content-header/entry-points/BylineClassificationFragmentType.ts';
import type { ByLineContributors_contentContributors$key } from '@confluence/content-header/entry-points/ByLineContributorsFragmentType.ts';
import {
	ContentTitle,
	ContentTitleFromFragment,
} from '@confluence/content-header/entry-points/ContentTitle.ts';
import type { ContentTitle_space$key } from '@confluence/content-header/entry-points/ContentTitleSpaceFragmentType.ts';
import { TitleAlignmentType } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import {
	getTitleContentProperty,
	useTitleContentPropertiesForPublishedPage,
} from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { ApprovalBanner } from '@confluence/approvals/entry-points/ApprovalBanner.ts';
import { LazyMeetingNotesAutomationDiscoveryFragment } from '@confluence/automation-discovery/entry-points/MeetingNotesAutomationDiscoveryFragment.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { useViewPageParams } from './useViewPageParams.ts';
import type { ContentTitleWithByLine_content$key } from './__generated__/ContentTitleWithByLine_content.graphql.ts';

export const ContentTitleWithByLineFragment: GraphQLTaggedNode = graphql`
	fragment ContentTitleWithByLine_content on Content
	@argumentDefinitions(useNewContentTopper: { type: "Boolean!", defaultValue: false }) {
		...ContentTitle_content @arguments(useNewContentTopper: $useNewContentTopper)
		...ByLine_content @arguments(useNewContentTopper: $useNewContentTopper)
		...ArchivedPageBannerV2Status_content
		...MeetingNotesAutomationDiscoveryFragment_content
	}
`;

type ByLinePassThroughProps = Pick<
	ComponentProps<typeof ByLine>,
	'hasByLineContributors' | 'hasByLineExtensions'
>;

type BylineClassificationFragmentProp = {
	bylineClassificationFragmentRef?: BylineClassification_content$key | null;
};

type AnalyticsBylineFragmentProp = {
	analyticsBylineFragmentRef?: unknown;
};

// Downstream packages are checked against the last generated content-header declaration.
const ByLineWithClassificationFragment = ByLine as FC<
	ComponentProps<typeof ByLine> & BylineClassificationFragmentProp & AnalyticsBylineFragmentProp
>;
const ByLineFromFragmentWithClassificationFragment = ByLineFromFragment as FC<
	ComponentProps<typeof ByLineFromFragment> &
		BylineClassificationFragmentProp &
		AnalyticsBylineFragmentProp
>;

type Props = Partial<ByLinePassThroughProps> &
	ComponentProps<typeof ContentTitle> & {
		hasCoverPicture?: boolean;
		pageWidthType?: string;
		isEmbeddedPage?: boolean;
		shouldShowBylineReactions?: boolean;
		contentType?: string | null;
		spaceId?: string | null;
		contentSubType?: string | null;
		hasByLine?: boolean;
		contentFragmentRef?: ContentTitleWithByLine_content$key | null;
		byLineContributors?: ByLineContributors_contentContributors$key | null;
		titleSpaceFragmentRef?: ContentTitle_space$key | null;
		bylineClassificationFragmentRef?: BylineClassification_content$key | null;
		contentClassificationFragmentRef?: ComponentProps<
			typeof ByLine
		>['contentClassificationFragmentRef'];
		reactionsContentFragmentRef?: ComponentProps<typeof ByLine>['reactionsContentFragmentRef'];
		meetingNotesAutomationDiscoveryFragmentRef?: ComponentProps<
			typeof LazyMeetingNotesAutomationDiscoveryFragment
		>['contentFragmentRef'];
		analyticsBylineFragmentRef?: AnalyticsBylineFragmentProp['analyticsBylineFragmentRef'];
	};

const BylineCenterStyles = xcss({
	justifyContent: 'center',
});

const ApprovalBannerStyles = xcss({
	marginBlockEnd: 'space.200',
});

const styles = cssMap({
	titleAreaBannerWrapper: {
		marginBlockEnd: token('space.200'),
	},
});

const TitleAreaBannerWrapper: FC<PropsWithChildren> = ({ children }) => (
	<Box xcss={styles.titleAreaBannerWrapper}>{children}</Box>
);

export const ContentTitleWithByLine: FC<Props> = (props) => (
	<SuspenseWithDataFetcher dataFetcherName="byline-apps">
		<ContentTitleWithByLineInner {...props} />
	</SuspenseWithDataFetcher>
);

const ContentTitleWithByLineInner: FC<Props> = ({
	contentFragmentRef,
	byLineContributors,
	titleSpaceFragmentRef,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	...props
}: Props) => {
	if (
		contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<ContentTitleWithByLineFragmentReader
				{...props}
				contentFragmentRef={contentFragmentRef}
				byLineContributors={byLineContributors}
				titleSpaceFragmentRef={titleSpaceFragmentRef}
				bylineClassificationFragmentRef={bylineClassificationFragmentRef}
				contentClassificationFragmentRef={contentClassificationFragmentRef}
				reactionsContentFragmentRef={reactionsContentFragmentRef}
				analyticsBylineFragmentRef={analyticsBylineFragmentRef}
			/>
		);
	}

	return (
		<ContentTitleWithByLineQuery
			{...props}
			bylineClassificationFragmentRef={bylineClassificationFragmentRef}
			contentClassificationFragmentRef={contentClassificationFragmentRef}
			reactionsContentFragmentRef={reactionsContentFragmentRef}
			analyticsBylineFragmentRef={analyticsBylineFragmentRef}
		/>
	);
};

const ContentTitleWithByLineQuery: FC<Props> = ({
	contentId,
	spaceKey,
	banner,
	hasByLineContributors,
	hasByLineExtensions,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	meetingNotesAutomationDiscoveryFragmentRef,
	analyticsBylineFragmentRef,
	hasByLine = true,
}: Props) => {
	const { versionOverride, embeddedContentRender } = useViewPageParams();
	const { isRovoLLMEnabled } = useSessionData();
	const shouldRenderMeetingNotesAutomationDiscovery =
		isRovoLLMEnabled &&
		contentType === 'page' &&
		!isEmbeddedPage &&
		fg('cc_automations_discovery_nudges');

	const { titleContentProperties } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitle
			banner={banner}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						<ByLineWithClassificationFragment
							contentId={contentId}
							versionOverride={versionOverride}
							embeddedContentRender={embeddedContentRender}
							hasByLineContributors={hasByLineContributors}
							hasByLineExtensions={hasByLineExtensions}
							shouldShowBylineReactions={shouldShowBylineReactions}
							contentType={contentType}
							contentSubType={contentSubType}
							spaceId={spaceId}
							bylineClassificationFragmentRef={bylineClassificationFragmentRef}
							contentClassificationFragmentRef={contentClassificationFragmentRef}
							reactionsContentFragmentRef={reactionsContentFragmentRef}
							analyticsBylineFragmentRef={analyticsBylineFragmentRef}
						/>
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
						{shouldRenderMeetingNotesAutomationDiscovery && (
							<LazyMeetingNotesAutomationDiscoveryFragment
								contentFragmentRef={meetingNotesAutomationDiscoveryFragmentRef}
								postOfficePlacementId="confluence-view-page-title-area-banner"
								WrapperComponent={TitleAreaBannerWrapper}
							/>
						)}
					</>
				)}
			</Box>
		</ContentTitle>
	);
};

const ContentTitleWithByLineFragmentReader: FC<
	Props & {
		contentFragmentRef: ContentTitleWithByLine_content$key;
	}
> = ({
	contentFragmentRef,
	byLineContributors,
	titleSpaceFragmentRef,
	bylineClassificationFragmentRef,
	contentClassificationFragmentRef,
	reactionsContentFragmentRef,
	analyticsBylineFragmentRef,
	contentId,
	spaceKey,
	banner,
	hasByLineContributors,
	hasByLineExtensions,
	headingContainer,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	shouldShowBylineReactions,
	contentType,
	contentSubType,
	spaceId,
	hasByLine = true,
}) => {
	const content = useFragment(ContentTitleWithByLineFragment, contentFragmentRef);
	const { isRovoLLMEnabled } = useSessionData();
	const shouldRenderMeetingNotesAutomationDiscovery =
		isRovoLLMEnabled &&
		contentType === 'page' &&
		!isEmbeddedPage &&
		fg('cc_automations_discovery_nudges');
	const { titleContentProperties } = getTitleContentProperty({ content: { nodes: [content] } });
	const bannerWithContentFragmentRef = isValidElement(banner)
		? React.cloneElement(banner as React.ReactElement<{ contentFragmentRef?: typeof content }>, {
				contentFragmentRef: content,
			})
		: banner;

	const isBylineCenterStyles =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	return (
		<ContentTitleFromFragment
			banner={bannerWithContentFragmentRef}
			contentId={contentId}
			spaceKey={spaceKey}
			headingContainer={headingContainer}
			hasCoverPicture={hasCoverPicture}
			pageWidthType={pageWidthType}
			isEmbeddedPage={isEmbeddedPage}
			contentFragmentRef={content}
			spaceFragmentRef={titleSpaceFragmentRef}
		>
			<Box
				xcss={[isBylineCenterStyles && BylineCenterStyles]}
				testId={
					isBylineCenterStyles ? 'centered-content-title-and-byline' : 'content-title-and-byline'
				}
			>
				{hasByLine && (
					<>
						<ByLineFromFragmentWithClassificationFragment
							contentId={contentId}
							contentFragmentRef={content}
							bylineClassificationFragmentRef={bylineClassificationFragmentRef}
							contentContributors={byLineContributors}
							contentClassificationFragmentRef={contentClassificationFragmentRef}
							reactionsContentFragmentRef={reactionsContentFragmentRef}
							analyticsBylineFragmentRef={analyticsBylineFragmentRef}
							hasByLineContributors={hasByLineContributors}
							hasByLineExtensions={hasByLineExtensions}
							shouldShowBylineReactions={shouldShowBylineReactions}
							contentType={contentType}
							contentSubType={contentSubType}
							spaceId={spaceId}
						/>
						{expValEquals('confluence_approvals_eap', 'isEnabled', true) && (
							<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
								<Box xcss={ApprovalBannerStyles}>
									<ApprovalBanner />
								</Box>
							</ErrorBoundary>
						)}
						{shouldRenderMeetingNotesAutomationDiscovery && (
							<LazyMeetingNotesAutomationDiscoveryFragment
								contentFragmentRef={content}
								postOfficePlacementId="confluence-view-page-title-area-banner"
								WrapperComponent={TitleAreaBannerWrapper}
							/>
						)}
					</>
				)}
			</Box>
		</ContentTitleFromFragment>
	);
};
